import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { graphql } from "../api/client";
import type { Cart, CartItem, Product } from "../api/types";
import { useAuth } from "../auth/AuthProvider";

interface CartState {
  cart: Cart | null;
  busy: boolean;
  message: string | null;
  add: (product: Product, quantity?: number) => Promise<void>;
  update: (productId: string, quantity: number) => Promise<void>;
  clear: () => void;
}

const CartContext = createContext<CartState | null>(null);
const CART_QUERY = `query Cart($id: ID!) { cart(id: $id) { id status currency subtotal { amountMinor currency formatted } items { id quantity lineTotal { amountMinor currency formatted } product { id slug sku name shortDescription description status stockQuantity material gemstone dimensions publishedModelVersionId images { id url viewLabel isPrimary } price { amountMinor currency formatted } } } } }`;
const CREATE_CART = `mutation { createCart { cart { id status currency subtotal { amountMinor currency formatted } items { id quantity lineTotal { amountMinor currency formatted } product { id slug sku name shortDescription description status stockQuantity material gemstone dimensions publishedModelVersionId images { id url viewLabel isPrimary } price { amountMinor currency formatted } } } } errors { code message } } }`;
const ADD_ITEM = `mutation Add($cartId: ID!, $productId: ID!, $quantity: Int!) { addCartItem(cartId: $cartId, productId: $productId, quantity: $quantity) { cart { id status currency subtotal { amountMinor currency formatted } items { id quantity lineTotal { amountMinor currency formatted } product { id slug sku name shortDescription description status stockQuantity material gemstone dimensions publishedModelVersionId images { id url viewLabel isPrimary } price { amountMinor currency formatted } } } } errors { code message } } }`;
const UPDATE_ITEM = `mutation Update($cartId: ID!, $productId: ID!, $quantity: Int!) { updateCartItem(cartId: $cartId, productId: $productId, quantity: $quantity) { cart { id status currency subtotal { amountMinor currency formatted } items { id quantity lineTotal { amountMinor currency formatted } product { id slug sku name shortDescription description status stockQuantity material gemstone dimensions publishedModelVersionId images { id url viewLabel isPrimary } price { amountMinor currency formatted } } } } errors { code message } } }`;

export function demoCart(product: Product, quantity: number, existing: Cart | null): Cart {
  const items = existing?.items ? [...existing.items] : [];
  const index = items.findIndex((item) => item.product.slug === product.slug);
  const nextQuantity = index >= 0 ? (items[index]?.quantity ?? 0) + quantity : quantity;
  const next: CartItem = {
    id: `demo-${product.slug}`,
    product,
    quantity: nextQuantity,
    lineTotal: {
      amountMinor: product.price.amountMinor * nextQuantity,
      currency: product.price.currency,
      formatted: `NZD ${((product.price.amountMinor * nextQuantity) / 100).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`,
    },
  };
  if (index >= 0) items[index] = next;
  else items.push(next);
  const subtotal = items.reduce((sum, item) => sum + item.lineTotal.amountMinor, 0);
  return {
    id: existing?.id ?? "demo-cart",
    status: "ACTIVE",
    currency: "NZD",
    items,
    subtotal: {
      amountMinor: subtotal,
      currency: "NZD",
      formatted: `NZD ${(subtotal / 100).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`,
    },
  };
}

export function CartProvider({ children }: PropsWithChildren) {
  const auth = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const cartId = window.localStorage.getItem("atelier-cart-id");
    if (!cartId || cartId === "demo-cart") return;
    void graphql<{ cart: Cart | null }>(CART_QUERY, { id: cartId }, auth.token)
      .then((data) => setCart(data.cart))
      .catch(() => window.localStorage.removeItem("atelier-cart-id"));
  }, [auth.token]);

  const ensureCart = useCallback(async (): Promise<Cart> => {
    if (cart) return cart;
    const data = await graphql<{ createCart: { cart: Cart; errors: Array<{ message: string }> } }>(
      CREATE_CART,
      {},
      auth.token,
    );
    if (data.createCart.errors.length) throw new Error(data.createCart.errors[0]?.message);
    window.localStorage.setItem("atelier-cart-id", data.createCart.cart.id);
    setCart(data.createCart.cart);
    return data.createCart.cart;
  }, [auth.token, cart]);

  const add = useCallback(
    async (product: Product, quantity = 1) => {
      setBusy(true);
      setMessage(null);
      try {
        const current = await ensureCart();
        const data = await graphql<{ addCartItem: { cart: Cart | null; errors: Array<{ message: string }> } }>(
          ADD_ITEM,
          { cartId: current.id, productId: product.id, quantity },
          auth.token,
        );
        if (!data.addCartItem.cart) throw new Error(data.addCartItem.errors[0]?.message);
        setCart(data.addCartItem.cart);
        setMessage(`${product.name} was added to your bag.`);
      } catch {
        const local = demoCart(product, quantity, cart);
        setCart(local);
        window.localStorage.setItem("atelier-cart-id", local.id);
        setMessage(`${product.name} was added in preview mode. Start the API to check out.`);
      } finally {
        setBusy(false);
      }
    },
    [auth.token, cart, ensureCart],
  );

  const update = useCallback(
    async (productId: string, quantity: number) => {
      if (!cart) return;
      if (cart.id === "demo-cart") {
        const items = cart.items
          .filter((item) => item.product.id !== productId || quantity > 0)
          .map((item) =>
            item.product.id === productId
              ? { ...item, quantity, lineTotal: { ...item.lineTotal, amountMinor: item.product.price.amountMinor * quantity } }
              : item,
          );
        const subtotal = items.reduce((sum, item) => sum + item.lineTotal.amountMinor, 0);
        setCart({ ...cart, items, subtotal: { ...cart.subtotal, amountMinor: subtotal, formatted: `NZD ${(subtotal / 100).toFixed(2)}` } });
        return;
      }
      setBusy(true);
      try {
        const data = await graphql<{ updateCartItem: { cart: Cart | null; errors: Array<{ message: string }> } }>(UPDATE_ITEM, { cartId: cart.id, productId, quantity }, auth.token);
        if (!data.updateCartItem.cart) throw new Error(data.updateCartItem.errors[0]?.message);
        setCart(data.updateCartItem.cart);
      } finally {
        setBusy(false);
      }
    },
    [auth.token, cart],
  );

  const clear = useCallback(() => {
    window.localStorage.removeItem("atelier-cart-id");
    setCart(null);
    setMessage(null);
  }, []);

  const value = useMemo(
    () => ({ cart, busy, message, add, update, clear }),
    [add, busy, cart, clear, message, update],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside CartProvider");
  return context;
}
